import type {
  CsvUserImportJobStatus,
  CsvUserImportResultItem,
  CsvUserImportRow,
  CsvUserImportRowOutcome,
} from '../../utils/csvUserImport';

export interface CsvUserImportSliceState {
  csvUserImportStatus: CsvUserImportJobStatus;
  csvUserImportRows: CsvUserImportRow[];
  /** Flat per-user items (pending → running → outcome) for the progress drawer. */
  csvUserImportResults: CsvUserImportResultItem[];
  csvUserImportSendInvites: boolean;
  csvUserImportCancelRequested: boolean;
  csvUserImportCreatedCount: number;
  csvUserImportSkippedCount: number;
  csvUserImportFailedCount: number;
  csvUserImportCancelledCount: number;
  csvUserImportDoneCount: number;
  csvUserImportTotal: number;
  /** Shown while job is active or until dismissed after complete. */
  isCsvUserImportDrawerVisible: boolean;
}

export interface CsvUserImportSlice extends CsvUserImportSliceState {
  beginCsvUserImport: (rows: CsvUserImportRow[], sendInvites: boolean) => void;
  markCsvUserImportItemRunning: (lineNumber: number) => void;
  appendCsvUserImportResult: (result: CsvUserImportResultItem) => void;
  requestCsvUserImportCancel: () => void;
  finishCsvUserImport: (finalStatus: 'completed' | 'cancelled') => void;
  dismissCsvUserImportDrawer: () => void;
  resetCsvUserImport: () => void;
}

const initialState: CsvUserImportSliceState = {
  csvUserImportStatus: 'idle',
  csvUserImportRows: [],
  csvUserImportResults: [],
  csvUserImportSendInvites: false,
  csvUserImportCancelRequested: false,
  csvUserImportCreatedCount: 0,
  csvUserImportSkippedCount: 0,
  csvUserImportFailedCount: 0,
  csvUserImportCancelledCount: 0,
  csvUserImportDoneCount: 0,
  csvUserImportTotal: 0,
  isCsvUserImportDrawerVisible: false,
};

const TERMINAL_STATUSES: ReadonlySet<CsvUserImportRowOutcome> = new Set([
  'created',
  'skippedAlreadyExists',
  'failed',
  'cancelled',
]);

function countsFor(status: CsvUserImportResultItem['status']): Partial<CsvUserImportSliceState> {
  switch (status) {
    case 'created':
      return { csvUserImportCreatedCount: 1 };
    case 'skippedAlreadyExists':
      return { csvUserImportSkippedCount: 1 };
    case 'failed':
      return { csvUserImportFailedCount: 1 };
    case 'cancelled':
      return { csvUserImportCancelledCount: 1 };
    default:
      return {};
  }
}

function seedPendingItems(rows: CsvUserImportRow[]): CsvUserImportResultItem[] {
  return rows.map((row) => ({
    lineNumber: row.lineNumber,
    email: row.email,
    firstName: row.firstName,
    lastName: row.lastName,
    status: 'pending' as const,
  }));
}

export const createCsvUserImportSlice = (set: any): CsvUserImportSlice => ({
  ...initialState,

  beginCsvUserImport: (rows, sendInvites) =>
    set({
      csvUserImportStatus: 'running',
      csvUserImportRows: rows,
      csvUserImportResults: seedPendingItems(rows),
      csvUserImportSendInvites: sendInvites,
      csvUserImportCancelRequested: false,
      csvUserImportCreatedCount: 0,
      csvUserImportSkippedCount: 0,
      csvUserImportFailedCount: 0,
      csvUserImportCancelledCount: 0,
      csvUserImportDoneCount: 0,
      csvUserImportTotal: rows.length,
      isCsvUserImportDrawerVisible: true,
    }),

  markCsvUserImportItemRunning: (lineNumber) =>
    set((state: CsvUserImportSliceState) => ({
      csvUserImportResults: state.csvUserImportResults.map((item) =>
        item.lineNumber === lineNumber && item.status === 'pending'
          ? { ...item, status: 'running' as const }
          : item,
      ),
    })),

  appendCsvUserImportResult: (result) =>
    set((state: CsvUserImportSliceState) => {
      if (!TERMINAL_STATUSES.has(result.status as CsvUserImportRowOutcome)) {
        return state;
      }
      const delta = countsFor(result.status);
      const exists = state.csvUserImportResults.some(
        (item) => item.lineNumber === result.lineNumber,
      );
      return {
        csvUserImportResults: exists
          ? state.csvUserImportResults.map((item) =>
              item.lineNumber === result.lineNumber
                ? {
                    ...item,
                    ...result,
                    firstName: result.firstName ?? item.firstName,
                    lastName: result.lastName ?? item.lastName,
                  }
                : item,
            )
          : [...state.csvUserImportResults, result],
        csvUserImportDoneCount: state.csvUserImportDoneCount + 1,
        csvUserImportCreatedCount:
          state.csvUserImportCreatedCount + (delta.csvUserImportCreatedCount ?? 0),
        csvUserImportSkippedCount:
          state.csvUserImportSkippedCount + (delta.csvUserImportSkippedCount ?? 0),
        csvUserImportFailedCount:
          state.csvUserImportFailedCount + (delta.csvUserImportFailedCount ?? 0),
        csvUserImportCancelledCount:
          state.csvUserImportCancelledCount + (delta.csvUserImportCancelledCount ?? 0),
      };
    }),

  requestCsvUserImportCancel: () => set({ csvUserImportCancelRequested: true }),

  finishCsvUserImport: (finalStatus) =>
    set({
      csvUserImportStatus: finalStatus,
      csvUserImportCancelRequested: false,
    }),

  dismissCsvUserImportDrawer: () =>
    set((state: CsvUserImportSliceState) => {
      if (state.csvUserImportStatus === 'running') return state;
      return { ...initialState };
    }),

  resetCsvUserImport: () => set({ ...initialState }),
});
